import {
  Box,
  Button as MuiButton,
  ButtonProps,
  CircularProgress,
  IconButton,
  IconButtonProps,
} from '@material-ui/core';
import styledConsts from 'app/styles/styledConsts';
import styledMixins from 'app/styles/styledMixins';
import { LocationObj } from 'app/tools/general.types';
import { getHistoryLocationObj } from 'app/tools/utils';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { Merge } from 'type-fest';
import SlideInOnRight from '../../frames/animation/SlideInOnRight';
import Icon from '../icon/Icon';
import IconLib from '../icon/icon-lib';

const buttonTypeHeights = {
  text: 40,
  slim: 50,
  normal: styledConsts.formItemHeight,
};

const SpacedIcon = styled(Icon)`
  padding-bottom: 0.1em;
`;

const CircularProgressBox = styled(Box)`
  float: right;
`;
const PendingCircle = styled(CircularProgress)`
  color: rgba(0, 0, 0, 0.26);
`;

type SpinnerStyling = { marginLeft?: string; marginRight?: string };
const PendingAnimation: React.VFC<{
  show: boolean;
  styling: SpinnerStyling;
}> = ({
  show,
  styling: { marginLeft = '12px', marginRight = '-4px' },
}) => (
  <SlideInOnRight
    in={show}
    width="1.3em"
    {...{ marginLeft, marginRight }}
  >
    <CircularProgressBox height="1.3em">
      <PendingCircle size="1.3em" />
    </CircularProgressBox>
  </SlideInOnRight>
);

type StyledButtonType = 'normal' | 'slim' | 'text' | 'plain';
type StyledButtonOpts = {
  $height?: number;
  $type: StyledButtonType;
  $greenBg: boolean;
  $fullWidth?: boolean;
  $noRadius?: boolean;
  component?: 'span';
};
const buttonPadding = {
  height: (p: StyledButtonOpts) =>
    p.$height
      ? `calc((${p.$height}px - 1.4em) / 2)`
      : p.$type !== 'plain' && '6px',
  width: (p: StyledButtonOpts) =>
    p.$height
      ? `${p.$height / 2}px`
      : p.$type === 'plain'
      ? 0
      : p.$type === 'text'
      ? '15px'
      : '25px',
};
const StyledButton = styled(MuiButton)<StyledButtonOpts>`
  ${p => !p.$noRadius && styledMixins.roundCorners}
  ${p =>
    p.$type !== 'plain' &&
    styledMixins.formItemHeight(
      p.$height || buttonTypeHeights[p.$type]
    )}
  ${p =>
    p.$fullWidth === true &&
    css`
      align-self: stretch;
    `}

  padding: ${buttonPadding.height} ${buttonPadding.width};

  ${p =>
    p.$greenBg &&
    css`
      &:hover {
        background-color: ${styledConsts.cPrimary};
      }
    `}
  ${p =>
    p.$type === 'plain' &&
    !p.color &&
    css`
      &,
      &:hover {
        background-color: transparent;
      }
    `}

  .MuiButton-label {
    text-transform: none;
    font-weight: 500;
    ${p =>
      p.$type === 'plain' &&
      css`
        ${styledMixins.fullsize}
        padding: 0;
      `}

    .MuiButton-endIcon {
      margin: 0;
      position: relative;
    }
  }
`;

type ClickEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>;

type Props = Merge<
  ButtonProps,
  {
    fullWidth?: boolean;
    slim?: boolean;
    startIcon?: IconLib;
    plain?: boolean;
    noRadius?: boolean;
    iconButton?: boolean | IconButtonProps;
    asSpan?: true;
    to?: string | LocationObj;
    replaceRoute?: boolean;
    navBackAction?: true;
    children?: React.ReactNode;
    pending?: boolean;
    spinnerStyling?: SpinnerStyling;
    height?: number;
  }
>;

export default function Button({
  children,
  className,
  slim,
  startIcon,
  noRadius,
  fullWidth = true,
  plain = false,
  iconButton = false,
  variant = 'contained',
  asSpan,
  color,
  onClick,
  to,
  replaceRoute,
  navBackAction,
  pending,
  spinnerStyling = {},
  disabled,
  height,
  ...props
}: Props): JSX.Element {
  const history = useHistory();

  const navigateAction = replaceRoute ? 'replace' : 'push';
  const locationObj = getHistoryLocationObj(to);

  const clickAction = locationObj
    ? (event: ClickEvent) => {
        onClick?.(event);
        history[navigateAction](locationObj);
      }
    : navBackAction
    ? (event: ClickEvent) => {
        onClick?.(event);
        history.goBack();
      }
    : onClick;

  const defaultColor =
    variant === 'text' || plain ? undefined : 'primary';
  const buttonColor = color || defaultColor;

  const childrenFormatted =
    typeof children === 'string' ? _.capitalize(children) : children;
  const disabledStyleActive = disabled || pending;

  const $type = plain
    ? 'plain'
    : variant === 'text'
    ? 'text'
    : slim
    ? 'slim'
    : 'normal';

  return iconButton ? (
    <IconButton
      onClick={clickAction}
      disabled={disabledStyleActive}
      aria-label={props['aria-label']}
      {...{ className }}
      {...iconButton}
    >
      {children}
    </IconButton>
  ) : (
    <StyledButton
      $greenBg={buttonColor === 'primary' && variant === 'contained'}
      $fullWidth={fullWidth}
      $height={height}
      $noRadius={noRadius}
      color={buttonColor}
      disableElevation
      onClick={clickAction}
      component={asSpan && 'span'}
      startIcon={
        startIcon && (
          <SpacedIcon inline icon={startIcon} emSize={1.3} />
        )
      }
      endIcon={
        <PendingAnimation show={!!pending} styling={spinnerStyling} />
      }
      disabled={disabledStyleActive}
      {...{ className, variant, $type }}
      {...props}
    >
      {childrenFormatted}
    </StyledButton>
  );
}
